import { useCallback, useEffect, useMemo, useState } from "react"
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  XAxis,
  YAxis,
} from "recharts"
import {
  IconClock,
  IconFileCheck,
  IconTrendingDown,
  IconTrendingUp,
  IconUsers,
} from "@tabler/icons-react"

import { api } from "@/lib/api"
import { Badge } from "@/components/ui/badge"
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@/components/ui/toggle-group"

// ── Types ────────────────────────────────────────────────────────────

interface StaffPerformance {
  staffName: string
  totalApplications: number
  certifiedCount: number
  inProgressCount: number
  avgDaysToComplete: number
  totalGrantAmount: number
  lastActivityDate: string | null
}

// ── Constants ────────────────────────────────────────────────────────

const RANGE_DAYS: Record<string, number> = {
  "7d": 7,
  "28d": 28,
  "90d": 90,
}

const BAR_COLORS = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
]

const chartConfig = {
  totalApplications: { label: "Applications", color: "var(--chart-1)" },
  certifiedCount: { label: "Certified", color: "var(--chart-2)" },
} satisfies ChartConfig

// ── Main component ───────────────────────────────────────────────────

export default function TeamPerformancePage() {
  const [timeRange, setTimeRange] = useState("28d")
  const [data, setData] = useState<StaffPerformance[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const days = RANGE_DAYS[timeRange] ?? 28
      const start = new Date()
      start.setDate(start.getDate() - days)
      const startStr = start.toISOString().split("T")[0]

      const result = await api.get<StaffPerformance[]>(
        `/api/statistics/staff-performance?startDate=${startStr}`
      )
      setData(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load staff data")
    } finally {
      setLoading(false)
    }
  }, [timeRange])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const stats = useMemo(() => {
    const activeStaff = data.length
    const totalApps = data.reduce((s, d) => s + d.totalApplications, 0)
    const totalCertified = data.reduce((s, d) => s + d.certifiedCount, 0)
    const totalPending = data.reduce((s, d) => s + d.inProgressCount, 0)
    const withDays = data.filter((d) => d.avgDaysToComplete > 0)
    const avgDays = withDays.length > 0
      ? withDays.reduce((s, d) => s + d.avgDaysToComplete, 0) / withDays.length
      : 0
    const avgCases = activeStaff > 0 ? totalApps / activeStaff : 0
    const totalAmount = data.reduce((s, d) => s + d.totalGrantAmount, 0)

    return { activeStaff, totalApps, totalCertified, totalPending, avgDays, avgCases, totalAmount }
  }, [data])

  const rangeLabel =
    timeRange === "7d"
      ? "last 7 days"
      : timeRange === "28d"
        ? "last 4 weeks"
        : "last 3 months"

  // Top 15 staff for the bar chart
  const chartData = useMemo(
    () => data.slice(0, 15).map((d) => ({
      name: d.staffName.length > 12 ? d.staffName.substring(0, 12) + "…" : d.staffName,
      fullName: d.staffName,
      totalApplications: d.totalApplications,
      certifiedCount: d.certifiedCount,
    })),
    [data]
  )

  if (loading) {
    return (
      <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
        <div className="flex items-center gap-2">
          <IconUsers className="size-6" />
          <h1 className="text-2xl font-bold">Team Performance</h1>
        </div>
        <div className="grid gap-4 md:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader><Skeleton className="h-4 w-28" /><Skeleton className="h-8 w-20" /></CardHeader>
            </Card>
          ))}
        </div>
        <Card>
          <CardHeader><Skeleton className="h-6 w-48" /></CardHeader>
          <CardContent><Skeleton className="h-[300px] w-full" /></CardContent>
        </Card>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
        <div className="flex items-center gap-2">
          <IconUsers className="size-6" />
          <h1 className="text-2xl font-bold">Team Performance</h1>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>Error</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
      {/* ── Header ── */}
      <div className="flex items-center justify-between px-4 lg:px-6">
        <div className="flex items-center gap-2">
          <IconUsers className="size-6" />
          <h1 className="text-2xl font-bold">Team Performance</h1>
        </div>
        <div className="flex items-center gap-2">
          <ToggleGroup
            type="single"
            value={timeRange}
            onValueChange={(v) => v && setTimeRange(v)}
            variant="outline"
            className="hidden *:data-[slot=toggle-group-item]:!px-4 md:flex"
          >
            <ToggleGroupItem value="7d">7 days</ToggleGroupItem>
            <ToggleGroupItem value="28d">4 weeks</ToggleGroupItem>
            <ToggleGroupItem value="90d">3 months</ToggleGroupItem>
          </ToggleGroup>
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-40 md:hidden" size="sm" aria-label="Select time range">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-xl">
              <SelectItem value="7d" className="rounded-lg">7 days</SelectItem>
              <SelectItem value="28d" className="rounded-lg">4 weeks</SelectItem>
              <SelectItem value="90d" className="rounded-lg">3 months</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <p className="text-muted-foreground px-4 lg:px-6 -mt-2">
        Intake staff activity based on benefit grant status updates — {rangeLabel}.
      </p>

      {/* ── Summary cards ── */}
      <div className="*:data-[slot=card]:from-primary/5 *:data-[slot=card]:to-card dark:*:data-[slot=card]:bg-card grid grid-cols-1 gap-4 px-4 *:data-[slot=card]:bg-gradient-to-t *:data-[slot=card]:shadow-xs lg:px-6 @xl/main:grid-cols-2 @5xl/main:grid-cols-4">
        <Card className="@container/card">
          <CardHeader>
            <CardDescription>Active Staff</CardDescription>
            <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
              {stats.activeStaff}
            </CardTitle>
            <CardAction>
              <Badge variant="outline">
                <IconUsers className="size-3" />
                {rangeLabel}
              </Badge>
            </CardAction>
          </CardHeader>
          <CardFooter className="flex-col items-start gap-1.5 text-sm">
            <div className="line-clamp-1 flex gap-2 font-medium">
              <IconUsers className="size-4" /> Staff with grant activity
            </div>
            <div className="text-muted-foreground">
              Avg. {stats.avgCases.toFixed(1)} cases per staff
            </div>
          </CardFooter>
        </Card>
        <Card className="@container/card">
          <CardHeader>
            <CardDescription>Total Grants Processed</CardDescription>
            <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
              {stats.totalApps.toLocaleString()}
            </CardTitle>
          </CardHeader>
          <CardFooter className="flex-col items-start gap-1.5 text-sm">
            <div className="line-clamp-1 flex gap-2 font-medium">
              <IconFileCheck className="size-4" /> {stats.totalCertified.toLocaleString()} certified, {stats.totalPending.toLocaleString()} pending
            </div>
            <div className="text-muted-foreground">
              ${stats.totalAmount.toLocaleString(undefined, { maximumFractionDigits: 0 })} total granted
            </div>
          </CardFooter>
        </Card>
        <Card className="@container/card">
          <CardHeader>
            <CardDescription>Avg. Time to Certify</CardDescription>
            <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
              {stats.avgDays > 0 ? `${stats.avgDays.toFixed(1)} days` : "—"}
            </CardTitle>
          </CardHeader>
          <CardFooter className="flex-col items-start gap-1.5 text-sm">
            <div className="line-clamp-1 flex gap-2 font-medium">
              <IconClock className="size-4" /> From application to certification
            </div>
            <div className="text-muted-foreground">Across all programs</div>
          </CardFooter>
        </Card>
        <Card className="@container/card">
          <CardHeader>
            <CardDescription>Certification Rate</CardDescription>
            <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
              {stats.totalApps > 0
                ? `${Math.round((stats.totalCertified / stats.totalApps) * 100)}%`
                : "—"}
            </CardTitle>
          </CardHeader>
          <CardFooter className="flex-col items-start gap-1.5 text-sm">
            <div className="line-clamp-1 flex gap-2 font-medium">
              {stats.totalCertified > stats.totalPending ? (
                <><IconTrendingUp className="size-4" /> More certifications than pending</>
              ) : (
                <><IconTrendingDown className="size-4" /> Backlog building</>
              )}
            </div>
            <div className="text-muted-foreground">Certified ÷ total processed</div>
          </CardFooter>
        </Card>
      </div>

      {/* ── Bar chart: top staff by volume ── */}
      {chartData.length > 0 && (
        <div className="px-4 lg:px-6">
          <Card className="@container/card">
            <CardHeader>
              <CardTitle>Top Staff by Volume</CardTitle>
              <CardDescription>
                Applications processed vs. certified — top {Math.min(15, chartData.length)} staff
              </CardDescription>
            </CardHeader>
            <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
              <ChartContainer
                config={chartConfig}
                className="aspect-auto h-[320px] w-full"
              >
                <BarChart data={chartData} layout="vertical" margin={{ left: 10 }}>
                  <CartesianGrid horizontal={false} />
                  <YAxis
                    dataKey="name"
                    type="category"
                    tickLine={false}
                    axisLine={false}
                    width={100}
                    tick={{ fontSize: 12 }}
                  />
                  <XAxis type="number" tickLine={false} axisLine={false} />
                  <ChartTooltip
                    content={
                      <ChartTooltipContent
                        labelFormatter={(_, payload) => {
                          const item = payload?.[0]?.payload as typeof chartData[0] | undefined
                          return item?.fullName ?? ""
                        }}
                      />
                    }
                  />
                  <Bar dataKey="totalApplications" radius={[0, 4, 4, 0]}>
                    {chartData.map((_, i) => (
                      <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} fillOpacity={0.3} />
                    ))}
                  </Bar>
                  <Bar dataKey="certifiedCount" radius={[0, 4, 4, 0]}>
                    {chartData.map((_, i) => (
                      <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ChartContainer>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── Staff table ── */}
      <div className="px-4 lg:px-6">
        <Card>
          <CardHeader>
            <CardTitle>Staff Detail</CardTitle>
            <CardDescription>
              Individual performance metrics — {rangeLabel}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {data.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No staff activity for this period.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 pr-4 font-medium">Staff</th>
                      <th className="pb-2 pr-4 font-medium text-right">Total</th>
                      <th className="pb-2 pr-4 font-medium text-right">Certified</th>
                      <th className="pb-2 pr-4 font-medium text-right">Pending</th>
                      <th className="pb-2 pr-4 font-medium text-right">Avg Days</th>
                      <th className="pb-2 pr-4 font-medium text-right">Granted</th>
                      <th className="pb-2 font-medium text-right">Last Active</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((s, i) => (
                      <tr key={`${s.staffName}-${i}`} className="border-b last:border-0">
                        <td className="py-2.5 pr-4 font-medium">{s.staffName}</td>
                        <td className="py-2.5 pr-4 text-right tabular-nums">{s.totalApplications}</td>
                        <td className="py-2.5 pr-4 text-right tabular-nums">{s.certifiedCount}</td>
                        <td className="py-2.5 pr-4 text-right tabular-nums">{s.inProgressCount}</td>
                        <td className="py-2.5 pr-4 text-right tabular-nums">
                          {s.avgDaysToComplete > 0 ? `${s.avgDaysToComplete}d` : "—"}
                        </td>
                        <td className="py-2.5 pr-4 text-right tabular-nums">
                          ${s.totalGrantAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="py-2.5 text-right text-muted-foreground">
                          {s.lastActivityDate
                            ? new Date(s.lastActivityDate).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                              })
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
